import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type { DataForwarderPolicy } from "@/types/preferences_types";

// ============================================
// API 응답/요청 타입
// ============================================

interface CreatePolicyStep1Request {
  policy_name: string;
  org_id: string;
  workspace_id?: string;
  channel?: string;
  sub_channel?: string;
  dw_account_id?: string;
  reports?: string;
  forwarding_type: string;
  external_id?: string;
}

interface UpdatePolicyStep1Request {
  policy_name: string;
  org_id: string;
  workspace_id?: string;
  channel?: string;
  sub_channel?: string;
  dw_account_id?: string;
  reports?: string;
}

interface UpdatePolicyStep2S3Request {
  forwarding_type: "s3";
  bucket_region: string;
  bucket_name: string;
  role_arn: string;
  prefix?: string;
  external_id: string;
}

interface UpdatePolicyStep2GCSRequest {
  forwarding_type: "gcs";
  bucket_name: string;
  target_service_account: string;
  prefix?: string;
}

type UpdatePolicyStep2Request = UpdatePolicyStep2S3Request | UpdatePolicyStep2GCSRequest;

interface ConnectionTestRequest {
  forwarding_type: string;
  bucket_name: string;
  // S3 fields
  bucket_region?: string;
  role_arn?: string;
  external_id?: string;
  // GCS fields
  target_service_account?: string;
  // Common
  prefix?: string;
}

interface ConnectionTestResponse {
  status: string;
  message: string;
}

export interface Organization {
  id: string;
  eng_identifier: string;
}

// ============================================
// API 함수들
// ============================================

/**
 * 모든 데이터 포워더 정책 조회
 */
async function fetchDataForwarderPolicies(): Promise<DataForwarderPolicy[]> {
  return api.get<DataForwarderPolicy[]>("/data-forwarder-policies/");
}

/**
 * 특정 정책 조회
 */
async function fetchDataForwarderPolicyById(policyId: number): Promise<DataForwarderPolicy> {
  return api.get<DataForwarderPolicy>(`/data-forwarder-policies/${policyId}`);
}

/**
 * AWS 리전 목록 조회
 */
async function fetchRegions(): Promise<string[]> {
  const response = await api.get<{ regions: string[] }>("/data-forwarder-policies/regions");
  return response.regions;
}

/**
 * Step 1: 정책 생성
 */
async function createPolicyStep1(request: CreatePolicyStep1Request): Promise<DataForwarderPolicy> {
  return api.post<DataForwarderPolicy>("/data-forwarder-policies/", request);
}

/**
 * Step 1: 정책 수정
 */
async function updatePolicyStep1(
  policyId: number,
  request: UpdatePolicyStep1Request
): Promise<DataForwarderPolicy> {
  return api.put<DataForwarderPolicy>(`/data-forwarder-policies/${policyId}/step1`, request);
}

/**
 * Step 2: S3 정보 저장
 */
async function updatePolicyStep2(
  policyId: number,
  request: UpdatePolicyStep2Request
): Promise<DataForwarderPolicy> {
  return api.put<DataForwarderPolicy>(`/data-forwarder-policies/${policyId}/step2`, request);
}

/**
 * Step 3: S3 연결 테스트
 */
async function testConnection(
  policyId: number,
  request: ConnectionTestRequest
): Promise<ConnectionTestResponse> {
  return api.post<ConnectionTestResponse>(`/data-forwarder-policies/${policyId}/test-connection`, request);
}

/**
 * 모든 조직 목록 조회
 */
async function fetchOrganizations(): Promise<Organization[]> {
  return api.get<Organization[]>("/organizations/");
}

/**
 * 정책 삭제 (여러 개)
 */
async function deletePolicies(policyIds: number[]): Promise<void> {
  return api.delete<void>("/data-forwarder-policies/", {
    data: policyIds,
  });
}

// ============================================
// Query 훅들
// ============================================

/**
 * 모든 데이터 포워더 정책 조회 Query
 */
export function useDataForwarderPoliciesQuery() {
  return useQuery({
    queryKey: queryKeys.dataForwarderPolicies.list(),
    queryFn: fetchDataForwarderPolicies,
  });
}

/**
 * 특정 정책 조회 Query
 */
export function useDataForwarderPolicyDetailQuery(policyId: number) {
  return useQuery({
    queryKey: queryKeys.dataForwarderPolicies.detail(policyId),
    queryFn: () => fetchDataForwarderPolicyById(policyId),
    enabled: !!policyId,
  });
}

/**
 * 조직 목록 조회 Query
 */
export function useOrganizationsQuery() {
  return useQuery({
    queryKey: queryKeys.organizations.list(),
    queryFn: fetchOrganizations,
    staleTime: Infinity,
  });
}

/**
 * 리전 목록 조회 Query
 */
export function useRegionsQuery() {
  return useQuery({
    queryKey: queryKeys.dataForwarderPolicies.regions(),
    queryFn: fetchRegions,
    staleTime: Infinity,
  });
}

// ============================================
// Mutation 훅들
// ============================================

/**
 * Step 1: 정책 생성 Mutation
 */
export function useCreatePolicyStep1Mutation() {
  return useMutation({
    mutationFn: createPolicyStep1,
    onError: (error: Error) => {
      toast({
        title: "정책 생성 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Step 1: 정책 수정 Mutation
 */
export function useUpdatePolicyStep1Mutation() {
  return useMutation({
    mutationFn: ({ policyId, request }: { policyId: number; request: UpdatePolicyStep1Request }) =>
      updatePolicyStep1(policyId, request),
    onError: (error: Error) => {
      toast({
        title: "정책 수정 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Step 2: S3 정보 저장 Mutation
 */
export function useUpdatePolicyStep2Mutation() {
  return useMutation({
    mutationFn: ({ policyId, request }: { policyId: number; request: UpdatePolicyStep2Request }) =>
      updatePolicyStep2(policyId, request),
    onError: (error: Error) => {
      toast({
        title: "S3 정보 저장 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Step 3: 연결 테스트 Mutation
 */
export function useTestConnectionMutation() {
  return useMutation({
    mutationFn: ({ policyId, request }: { policyId: number; request: ConnectionTestRequest }) =>
      testConnection(policyId, request),
    onError: (error: Error) => {
      toast({
        title: "연결 테스트 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * 정책 삭제 Mutation
 */
export function useDeletePoliciesMutation() {
  return useMutation({
    mutationFn: deletePolicies,
    onError: (error: Error) => {
      toast({
        title: "삭제 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// Export request types for use in components
export type {
  CreatePolicyStep1Request,
  UpdatePolicyStep1Request,
  UpdatePolicyStep2Request,
  UpdatePolicyStep2S3Request,
  UpdatePolicyStep2GCSRequest,
  ConnectionTestRequest,
  ConnectionTestResponse,
};
